export const FINANCIAL_PRICING_PERMISSION_KEYS = [
  "financial_control.risks.view",
  "financial_control.view",
  "financial_control.manage",
];

export const MANAGEABLE_PRICING_SCOPE_OPTIONS = [
  { value: "tenant_default", label: "Tenantstandaard" },
  { value: "pricing_profile", label: "Prijsprofiel" },
  { value: "client", label: "Klant" },
  { value: "project", label: "Project" },
];

export function hasFinancialPricingAccess(permissions = []) {
  const permissionSet = new Set(
    (Array.isArray(permissions) ? permissions : [])
      .map((item) => String(item || "").trim().toLowerCase())
      .filter(Boolean)
  );
  return FINANCIAL_PRICING_PERMISSION_KEYS.some((key) => permissionSet.has(key));
}

export function getPricingScopeLabel(scopeType = "") {
  switch (String(scopeType || "").trim().toLowerCase()) {
    case "tenant_default":
      return "Tenantstandaard";
    case "pricing_profile":
      return "Prijsprofiel";
    case "client":
      return "Klant";
    case "project":
      return "Project";
    case "location":
      return "Locatie";
    case "contract":
      return "Contract";
    default:
      return scopeType || "-";
  }
}

export function getPricingAgreementDerivedStatus(agreement = {}, referenceDate = new Date()) {
  if (agreement?.derivedStatus?.key) {
    return agreement.derivedStatus;
  }

  const effectiveDate = normalizeDate(referenceDate) || new Date();
  const status = String(agreement?.status || "draft").trim().toLowerCase();
  const effectiveTo = normalizeDate(agreement?.effectiveTo);

  if (status === "archived") {
    return { key: "archived", label: "Ingetrokken" };
  }
  if (status === "draft") {
    return { key: "draft", label: "Concept" };
  }
  if (effectiveTo && effectiveTo < effectiveDate) {
    return { key: "expired", label: "Verlopen" };
  }
  return { key: "active", label: "Actief" };
}

export function formatPricingDate(value) {
  const date = normalizeDate(value);
  if (!date) return "-";
  return date.toLocaleDateString("nl-NL");
}

export function formatPricingDateTime(value) {
  const date = normalizeDate(value);
  if (!date) return "-";
  return date.toLocaleString("nl-NL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function formatPricingValidity(agreement = {}) {
  const from = formatPricingDate(agreement?.effectiveFrom);
  const to = formatPricingDate(agreement?.effectiveTo);
  if (from === "-" && to === "-") return "Doorlopend";
  if (from !== "-" && to === "-") return `Vanaf ${from}`;
  if (from === "-" && to !== "-") return `Tot ${to}`;
  return `${from} t/m ${to}`;
}

export function getPricingSourceBadge(snapshot = null) {
  const sellSource = snapshot?.sellSource || null;
  const costSource = snapshot?.costSource || null;
  const sourceType = String(
    sellSource?.sourceType || costSource?.sourceType || sellSource?.scopeType || costSource?.scopeType || ""
  )
    .trim()
    .toLowerCase();
  const scopeType = String(sellSource?.scopeType || costSource?.scopeType || "").trim().toLowerCase();

  if (sourceType === "legacy_manual") {
    return { key: "legacy_manual", label: "Legacy handmatig", tone: "legacy" };
  }

  if (["line_input", "other_cost_input"].includes(sourceType)) {
    return { key: "manual_override", label: "Handmatige afwijking", tone: "manual" };
  }

  if (scopeType === "project") {
    return { key: "project", label: "Projectprijs", tone: "project" };
  }

  if (scopeType === "client") {
    return { key: "client", label: "Klantprijs", tone: "client" };
  }

  if (scopeType === "pricing_profile") {
    return { key: "pricing_profile", label: "Prijsprofiel", tone: "profile" };
  }

  return { key: "tenant_default", label: "Tenantstandaard", tone: "default" };
}

export function getPricingSourceSummary(snapshot = null) {
  const badge = getPricingSourceBadge(snapshot);
  const source = snapshot?.sellSource || snapshot?.costSource || {};
  return {
    ...badge,
    agreementId: source?.agreementId || null,
    agreementVersion: source?.agreementVersion || null,
    ruleId: source?.ruleId || null,
    scopeType: source?.scopeType || null,
    sourceType: source?.sourceType || null,
    pricingDate: snapshot?.pricingDate || null,
  };
}

export function computePricingAgreementStats(agreements = [], referenceDate = new Date()) {
  return (Array.isArray(agreements) ? agreements : []).reduce(
    (acc, agreement) => {
      const derived = getPricingAgreementDerivedStatus(agreement, referenceDate);
      if (derived.key === "active") acc.active += 1;
      if (derived.key === "draft") acc.draft += 1;
      if (derived.key === "expired") acc.expired += 1;
      if (derived.key === "archived") acc.archived += 1;
      if (Array.isArray(agreement?.warnings) && agreement.warnings.length > 0) {
        acc.warnings += 1;
      }
      return acc;
    },
    {
      active: 0,
      draft: 0,
      expired: 0,
      archived: 0,
      warnings: 0,
    }
  );
}

function normalizeDate(value) {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}
